import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { CacheKeys } from '@/lib/cache'
import { useUploadLossRunFiles } from '@/models/lossRunRequest'
import type { FillCardForm } from '../App/Customers/FillApplication/useFillCardSave'

export interface LossRunUpload extends FillCardForm {
  files: File[]
  addFiles: (files: File[]) => void
}

/** Loss run reports the customer has picked, sent when they Save rather than as they pick them. */
export function useLossRunUpload(orgId: string, applicationId: string | undefined): LossRunUpload {
  const queryClient = useQueryClient()
  // Adds to what's on file: the organisation endpoint replaces every claim and open request first.
  const upload = useUploadLossRunFiles(applicationId)
  const [files, setFiles] = useState<File[]>([])
  const [saving, setSaving] = useState(false)

  async function save() {
    if (files.length === 0 || !applicationId) return true
    setSaving(true)
    try {
      await upload.mutateAsync(files)
      toast.success('Loss run reports uploaded — updating your application. This may take a few minutes.')
      setFiles([])
      // Only the reports list: it polls while they're read, and nothing else on the page shows them.
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.LOSS_RUN_REQUESTS, orgId] })
      return true
    } catch {
      toast.error("We couldn't upload your loss run reports. Try again.")
      return false
    } finally {
      setSaving(false)
    }
  }

  return {
    files,
    addFiles: (picked) => setFiles((current) => [...current, ...picked]),
    isDirty: files.length > 0,
    saving,
    save,
    discard: () => setFiles([]),
  }
}
